// Clan Castle: request troops (and spells from Sol Hall 6) from clanmates every 10 minutes, donate to theirs.
// Data (shared db): ccreq/{uid} = my open request, ccdon/{uid} = my donations (each player writes only their own doc).
import { t } from './i18n.js';
import { TR, SPELLS, CASTLE_CAP, castleSpellCap } from './data.js';
import { S, save } from './state.js';
import { core, tLvl } from './economy.js';
import { esc, fmt, toast, renderHeader, troopDot, spellIcon } from './ui.js';

const DON_KEEP = 60;
const reqCooldown = () => perks().cd * 60e3; // 10 minutes, shorter with clan perks
let db = null, uid = null, q = null, reqs = {}, dons = {}, onChange = () => {}, perks = () => ({ cd: 10, cap: 0, scap: 0 });

const castle = () => S.blds.find(b => b.type === 'castle' && b.lvl >= 1);
const cap = () => { const c = castle(); return c ? CASTLE_CAP[Math.min(c.lvl, CASTLE_CAP.length - 1)] + perks().cap : 0; };
const scap = () => { const c = castle(); const n = c ? castleSpellCap(c.lvl, core().lvl) : 0; return n ? n + perks().scap : 0; };
const contents = () => { S.castle = S.castle || { troops: [], spells: {} }; S.castle.troops = S.castle.troops || []; S.castle.spells = S.castle.spells || {}; return S.castle; };
const usedSpace = () => contents().troops.reduce((a, x) => a + (TR[x.k] ? TR[x.k].sp : 0), 0);
const usedSpells = () => Object.values(contents().spells).reduce((a, b) => a + b, 0);
const cooldownLeft = () => Math.max(0, (S.ccLastReq || 0) + reqCooldown() - Date.now());
const skipCost = () => Math.max(1, Math.ceil(cooldownLeft() / 60e3));

function bind(o) { db = o.db; uid = o.uid; q = o.q; onChange = o.onChange || onChange; if (o.perks) perks = o.perks; }
const donations = () => dons;
function setReqs(o) { reqs = o; }
function setDons(o) { dons = o; applyDonations(); }

/** Ask the clan for troops. Pays gems to skip the 10-minute cooldown when needed. */
function requestTroops(skip) {
  if (!db || !uid) { toast(t('offline')); return false; }
  if (!S.clan) { toast(t('ccNeedClan')); return false; }
  if (!castle()) { toast(t('ccBuildFirst')); return false; }
  if (cooldownLeft() > 0) { if (!skip) { toast(t('ccCooldown', { m: Math.ceil(cooldownLeft() / 60e3) })); return false; }
    const g = skipCost(); if (S.gems < g) { toast(t('notEnoughGems')); return false; } S.gems -= g; }
  const now = Date.now(); S.ccLastReq = now; save(); renderHeader();
  const body = { clan: S.clan, name: S.name, t: now, cap: cap(), have: usedSpace(), scap: scap(), shave: usedSpells() };
  reqs = { ...reqs, [uid]: body }; q(() => db.doc('ccreq/' + uid).set(body)); toast(t('ccRequested')); onChange(); return true;
}

// Donations to one request (all donors), so nobody over-fills it.
function filledFor(to, reqT) { let sp = 0, spells = 0; for (const d in dons) for (const x of ((dons[d] && dons[d].list) || [])) if (x && x.to === to && x.req === reqT) { if (x.spell) spells += 1; else sp += (TR[x.k] ? TR[x.k].sp : 0); } return { sp, spells }; }

/** Give one troop (or spell) from my army to a clanmate's request. */
function donate(to, k, spell) {
  const r = reqs[to]; if (!r || r.clan !== S.clan || to === uid) return false;
  const f = filledFor(to, r.t);
  if (spell) { if (!(S.spells || {})[k] || f.spells + (r.shave || 0) >= (r.scap || 0)) return false; S.spells[k]--; }
  else { const sp = TR[k] && TR[k].sp; if (!(S.army || {})[k] || f.sp + (r.have || 0) + sp > (r.cap || 0)) { toast(t('ccFull')); return false; } S.army[k]--; if (!S.army[k]) delete S.army[k]; }
  const cur = (dons[uid] && dons[uid].list) || [];
  const entry = { to, req: r.t, k, L: spell ? 1 : tLvl(k), spell: !!spell, t: Date.now() };
  const body = { list: cur.slice(-(DON_KEEP - 1)).concat([entry]) }; dons = { ...dons, [uid]: body };
  q(() => db.doc('ccdon/' + uid).set(body)); save(); toast(t('ccDonated', { n: r.name || '?' })); onChange(); return true;
}

/** Move donations made to my latest request into my castle (once each, up to capacity). */
function applyDonations() {
  if (!uid || !S.ccLastReq) return; const c = contents(); S.ccApplied = S.ccApplied || []; let n = 0;
  for (const d in dons) { if (d === uid) continue; for (const x of ((dons[d] && dons[d].list) || [])) {
    if (!x || x.to !== uid || x.req !== S.ccLastReq) continue; const key = d + ':' + x.t; if (S.ccApplied.includes(key)) continue; S.ccApplied.push(key);
    if (x.spell) { if (SPELLS[x.k] && usedSpells() < scap()) { c.spells[x.k] = (c.spells[x.k] || 0) + 1; n++; } }
    else if (TR[x.k] && !TR[x.k].hidden && usedSpace() + TR[x.k].sp <= cap()) { c.troops.push({ k: x.k, L: Math.max(1, Math.min(TR[x.k].lmax, x.L | 0)) }); n++; } } }
  if (n) { S.ccApplied = S.ccApplied.slice(-300); save(); toast(t('ccReceived', { n })); onChange(); }
}

/** Castle panel inside the building info sheet. */
function castleHTML() {
  const c = contents(), left = cooldownLeft();
  const tr = {}; for (const x of c.troops) tr[x.k] = (tr[x.k] || 0) + 1;
  let h = `<div class="ccbox"><div class="cchead"><b>🏰 ${esc(t('ccTroops'))}</b><span>${usedSpace()}/${cap()}${scap() ? ` · ✨ ${usedSpells()}/${scap()}` : ''}</span></div>
    <div class="pbar"><i style="width:${cap() ? usedSpace() / cap() * 100 : 0}%"></i></div><div class="ccitems">`;
  for (const k in tr) h += `<span class="ccit">${troopDot(k, 18)}<b>×${tr[k]}</b></span>`;
  for (const k in c.spells) h += `<span class="ccit">${spellIcon(k, 30)}<b>×${c.spells[k]}</b></span>`;
  if (!c.troops.length && !usedSpells()) h += `<span class="mt">${esc(t('ccEmpty'))}</span>`;
  h += `</div><div class="btnrow">`;
  if (left > 0) h += `<button class="sm" id="ccReq" disabled>⏱ ${Math.floor(left / 60e3)}:${String(Math.floor(left % 60e3 / 1e3)).padStart(2, '0')}</button><button class="sm gemb" id="ccSkip">${esc(t('ccRequest'))} · 💎 ${skipCost()}</button>`;
  else h += `<button class="sm go" id="ccReq" style="font-size:14px">${esc(t('ccRequest'))}</button>`;
  h += `</div><p class="note">${esc(t('ccInfo', { m: perks().cd }))}</p></div>`;
  return h;
}
function bindCastle(root, rerender) { const r = root.querySelector('#ccReq'), s = root.querySelector('#ccSkip');
  if (r) r.onclick = () => { if (requestTroops(false)) rerender(); }; if (s) s.onclick = () => { if (requestTroops(true)) rerender(); }; }

/** Open clanmate requests with donate buttons (shown in the Clan sheet). */
function requestsHTML() {
  const now = Date.now(), list = Object.entries(reqs).filter(([id, r]) => id !== uid && r && r.clan === S.clan && now - (r.t || 0) < 8 * 3600e3);
  if (!list.length) return '';
  let h = `<div class="ccreqs"><b>🏰 ${esc(t('ccRequests'))}</b>`;
  for (const [id, r] of list) { const f = filledFor(id, r.t), full = f.sp + (r.have || 0), sfull = f.spells + (r.shave || 0);
    h += `<div class="ccreq"><div class="grow"><div class="nm">${esc(String(r.name || '?').slice(0, 20))}</div><div class="pbar"><i style="width:${Math.min(100, full / Math.max(1, r.cap) * 100)}%"></i></div><small>${full}/${r.cap | 0}${r.scap ? ` · ✨ ${sfull}/${r.scap | 0}` : ''}</small></div><div class="ccdon">`;
    for (const k in (S.army || {})) if (S.army[k] > 0 && TR[k] && full + TR[k].sp <= r.cap) h += `<button class="sm" data-don="${id}|${k}|0" title="${esc(t('t_' + k))}">${troopDot(k, 14)}<span>${S.army[k]}</span></button>`;
    if (r.scap && sfull < r.scap) for (const k in (S.spells || {})) if (S.spells[k] > 0) h += `<button class="sm" data-don="${id}|${k}|1" title="${esc(t('sp_' + k))}">${spellIcon(k, 24)}<span>${S.spells[k]}</span></button>`;
    h += `</div></div>`; }
  return h + '</div>';
}
function bindRequests(root, rerender) { root.querySelectorAll('[data-don]').forEach(b => b.onclick = () => { const [to, k, sp] = b.dataset.don.split('|'); if (donate(to, k, sp === '1')) rerender(); }); }

/** Take the castle's contents into battle (cleared after the raid if deployed). */
const castleArmy = () => contents().troops.slice();
const castleSpells = () => ({ ...contents().spells });

export { bind, setReqs, setDons, requestTroops, donate, applyDonations, castleHTML, bindCastle, requestsHTML, bindRequests, castleArmy, castleSpells, contents as castleContents, cap as castleCap, reqCooldown, donations };
